import { FormData } from 'components/forms/DynamicForm'
import { FlowTypes, STEPS } from '@/lib/constants'
import { getOddsDotStatus } from 'components/fixtures/HorseRacing/ODDS/OddsStatus'
import {
  CheckboxQuestion,
  FeatureFlags,
  GameINFOV2,
  GameStatistics,
  GameType,
  GolfData,
  LeaderboardRecord,
  Participant,
  PartnerPageData,
  Question,
  QuestionType,
  Round,
  RoundStatus,
  RoundStatusLabels,
  ScoringOption,
  SelectionByMatchRecord,
  SelectionRecord,
  SelectionStatWithHomeAwayTeams,
  SelectionStatWithUnit,
  SelectionUpdateObj,
  SimpleRound,
  Unit,
} from './types'
import { IPrizeDrawTicket, ITicket } from './prize-draw/ticket'

type GameWithKind = {
  id: number
  kind: 'game' | 'round'
  roundId?: number
}

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const secondsRemaining = seconds % 60
  return `${minutes}:${secondsRemaining.toString().padStart(2, '0')}`
}

export const getFirstUpcomingRound = (game: GameINFOV2) => {
  return (
    game.rounds.find((round) => new Date(round.start_date) > new Date()) ??
    game.rounds[game.active_round_index]
  )
}

export const getFirstUpcomingOrActiveRound = (game: GameINFOV2) => {
  const currentRound = game.rounds[game.active_round_index]

  const round =
    game.rounds.find(
      (round) => round.status === RoundStatus.UPCOMING || round.status === RoundStatus.ACTIVE,
    ) ?? currentRound
  return round
}

export const getCurrentRound = (gameInfo: GameINFOV2) => {
  const currentRoundByIndex = gameInfo.rounds[gameInfo.active_round_index]
  if (!currentRoundByIndex) {
    return gameInfo.rounds[0]
  }
  if (
    gameInfo.active_round_index !== 0 &&
    [RoundStatus.ACTIVE, RoundStatus.FINISHED].every((s) => s !== currentRoundByIndex.status)
  ) {
    return gameInfo.rounds[gameInfo.active_round_index - 1]
  }
  return currentRoundByIndex
}

export const getSelectedUnit = (data: SelectionRecord, matchId: number, contestants: Unit[]) => {
  const selectionData = data?.matches?.[matchId] ?? {}

  const selected_unit = selectionData?.selections?.map((selection) => {
    return {
      unit: contestants.find((unit) => unit.id === selection.unit_id),
      points: selectionData.points,
    }
  })
  return selected_unit?.[0] ?? { unit: undefined, points: null, holes_played: null }
}

export const getSelectedUnitByMatch = (data: SelectionByMatchRecord, contestants: Unit[]) => {
  const selectionData = data ?? {}

  const selected_unit = selectionData?.selections?.map((selection) => {
    return {
      unit: contestants.find((unit) => unit.id === selection.unit_id),
      points: selectionData.points,
    }
  })
  return selected_unit?.[0] ?? { unit: undefined, points: null, holes_played: null }
}

export function stringToColor(string: string) {
  let hash = 0
  let i

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.slice(-2)
  }
  /* eslint-enable no-bitwise */

  return color
}

export const getSelectedUnitFromPersistedData = (
  data: Record<'selections', SelectionUpdateObj[]>,
  matchId: number,
  contestants: Unit[],
) => {
  const selection = data?.selections?.find((selection) => selection.match_id === matchId)
  const selectedUnit = {
    unit: contestants.find((unit) => unit.id === selection?.unit_id),
    points: null,
    holes_played: null,
  }
  return selectedUnit
}

export const getUserTiebreaker = (data: SelectionRecord, userId: number) => {
  return data?.tiebreakers[0]?.answer ?? null
}

const SCORE_GREEN = '#6BAB69'
const SCORE_GREEN_BG = '#6BAB694D'
const SCORE_GREEN_DARK = '#3F7A3D'

const SCORE_ORANGE = '#F7953B'
const SCORE_ORANGE_BG = '#F7953B33'
const SCORE_ORANGE_DARK = '#A9570C'

const SCORE_RED = '#B03030'
const SCORE_RED_BG = '#B030304D'
const SCORE_RED_DARK = '#6A2323'

const SCORE_GOLD = '#FFE600'
const SCORE_GOLD_BG = '#FFE60033'
const SCORE_GOLD_DARK = '#D8AF1E'
const SCORE_GOLD_BORDER_PTW = '#dbc604'
const SCORE_GOLD_BORDER_DRAFT = '#b18e12'

const SCORE_TEXT_DARK = '#353535'

export const getSRScore = (scoringOption: ScoringOption) => {
  switch (scoringOption) {
    case 'GOLD':
      return {
        border: SCORE_GREEN,
        color: SCORE_GREEN_BG,
        gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
      }
    case 'SILVER':
      return {
        border: SCORE_ORANGE,
        color: SCORE_ORANGE_BG,
        gradient: `linear-gradient(90deg, ${SCORE_ORANGE} 0%, ${SCORE_ORANGE_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_ORANGE_BG} inset;`,
      }
    case 'BRONZE':
      return {
        border: SCORE_ORANGE,
        color: SCORE_ORANGE_BG,
        gradient: `linear-gradient(90deg, ${SCORE_ORANGE} 0%, ${SCORE_ORANGE_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_ORANGE_BG} inset;`,
      }
    case 'ZERO':
      return {
        border: SCORE_RED,
        color: SCORE_RED_BG,
        gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
      }
    default:
      return { color: 'white' }
  }
}
export const getPTWScore = (scoringOption: ScoringOption, page: PartnerPageData | null) => {
  if (page && page.feature_flags.is_star_sports_page) {
    switch (scoringOption) {
      case 'GOLD':
        return {
          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
        }
      case 'SILVER':
        return {
          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
        }
      case 'BRONZE':
        return {
          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
        }
      case 'ZERO':
        return {
          border: SCORE_RED,
          color: SCORE_RED_BG,
          gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
        }
      default:
        return { color: 'white' }
    }
  }
  switch (scoringOption) {
    case 'GOLD':
      return {
        border: SCORE_GREEN,
        color: SCORE_GREEN_BG,
        gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
      }
    case 'SILVER':
      return {
        border: SCORE_GOLD_BORDER_PTW,
        color: SCORE_GOLD_BG,
        gradient: `linear-gradient(90deg, ${SCORE_GOLD} 0%, ${SCORE_GOLD_DARK} 100%)`,
        shadow: `1px 2px 16px 0px ${SCORE_GOLD_BG} inset;`,
      }
    case 'BRONZE':
      return {
        border: SCORE_ORANGE,
        color: SCORE_ORANGE_BG,
        gradient: `linear-gradient(90deg, ${SCORE_ORANGE} 0%, ${SCORE_ORANGE_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_ORANGE_BG} inset;`,
      }
    case 'ZERO':
      return {
        border: SCORE_RED,
        color: SCORE_RED_BG,
        gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
        shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
      }
    default:
      return { color: 'white' }
  }
}
export const getOddsStyle = (points: number | null | undefined, odds?: number | string | null) => {
  const oddsNum = typeof odds === 'string' ? parseFloat(odds) : odds

  const isFirst = points != null && oddsNum != null && Math.abs(points / oddsNum - 1) < 0.001

  const status = isFirst ? 'green' : getOddsDotStatus(points, oddsNum)

  const styles = {
    green: {
      background: '#E8F5E9',
      border: '#4CAF50',
      gradient: 'linear-gradient(90deg,#4CAF50,#2E7D32)',
      text: '#2E7D32',
    },
    yellow: {
      background: '#FFF8E1',
      border: '#F0B100',
      gradient: 'linear-gradient(90deg,#F0B100,#C49000)',
      text: '#C49000',
    },
    red: {
      background: '#FDECEA',
      border: '#F44336',
      gradient: 'linear-gradient(90deg,#F44336,#C62828)',
      text: '#C62828',
    },
    grey: {
      background: '#F5F5F5',
      border: '#999999',
      gradient: 'linear-gradient(90deg,#999999,#777777)',
      text: '#666',
    },
  }

  return styles[status]
}
export const isHighestUnitScoreInMatch = (unit: Unit | undefined, units: Unit[]) => {
  if (unit?.score === undefined || unit?.score === null) return false
  const maxScore = Math.max(...units.map((u) => u.score ?? -Infinity))
  return unit.score === maxScore
}

export const getDraftScore = (
  points: number | string,
  isGolfGame?: boolean,
  isHighestScore?: boolean,
) => {
  if (isGolfGame) {
    switch (true) {
      case typeof points === 'number' && points > 0:
        return {
          border: SCORE_RED,
          text: SCORE_TEXT_DARK,
          color: SCORE_RED_BG,
          gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
          points,
        }
      case points === 0:
        return {
          border: SCORE_GOLD_BORDER_DRAFT,
          text: SCORE_TEXT_DARK,
          color: SCORE_GOLD_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GOLD} 0%, ${SCORE_GOLD_DARK} 100%)`,
          shadow: `1px 2px 16px 0px ${SCORE_GOLD_BG} inset;`,
          points: 'E',
        }
      case typeof points === 'number' && points < 0:
        return {
          text: SCORE_TEXT_DARK,

          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
          points,
        }
      case points === '-':
        return {
          points: '-',
          color: 'white',

          text: SCORE_TEXT_DARK,
        }
      default:
        return { points: '-', color: 'white', text: SCORE_TEXT_DARK }
    }
  } else if (isHighestScore !== undefined) {
    // Results tab: coloring is relative to the other scores in the match, rather than fixed thresholds.
    switch (true) {
      case typeof points === 'number' && points === 0:
        return {
          border: SCORE_RED,
          text: SCORE_TEXT_DARK,
          color: SCORE_RED_BG,
          gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
          points,
        }
      case typeof points === 'number' && isHighestScore:
        return {
          text: SCORE_TEXT_DARK,

          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
          points,
        }
      case typeof points === 'number':
        return {
          border: SCORE_GOLD_BORDER_DRAFT,
          text: SCORE_TEXT_DARK,
          color: SCORE_GOLD_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GOLD} 0%, ${SCORE_GOLD_DARK} 100%)`,
          shadow: `1px 2px 16px 0px ${SCORE_GOLD_BG} inset;`,
          points,
        }
      case points === '-':
        return {
          points: '-',
          color: 'white',

          text: SCORE_TEXT_DARK,
        }
      default:
        return { points: '-', color: 'white', text: SCORE_TEXT_DARK }
    }
  } else {
    switch (true) {
      case typeof points === 'number' && points === 0:
        return {
          border: SCORE_RED,
          text: SCORE_TEXT_DARK,
          color: SCORE_RED_BG,
          gradient: `linear-gradient(90deg, ${SCORE_RED} 0%, ${SCORE_RED_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_RED_BG} inset;`,
          points,
        }
      case typeof points === 'number' && points >= 1 && points <= 10:
        return {
          border: SCORE_GOLD_BORDER_DRAFT,
          text: SCORE_TEXT_DARK,
          color: SCORE_GOLD_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GOLD} 0%, ${SCORE_GOLD_DARK} 100%)`,
          shadow: `1px 2px 16px 0px ${SCORE_GOLD_BG} inset;`,
          points,
        }
      case typeof points === 'number' && points >= 11:
        return {
          text: SCORE_TEXT_DARK,

          border: SCORE_GREEN,
          color: SCORE_GREEN_BG,
          gradient: `linear-gradient(90deg, ${SCORE_GREEN} 0%, ${SCORE_GREEN_DARK} 100%);`,
          shadow: `1px 2px 16px 0px ${SCORE_GREEN_BG} inset;`,
          points,
        }
      case points === '-':
        return {
          points: '-',
          color: 'white',

          text: SCORE_TEXT_DARK,
        }
      default:
        return { points: '-', color: 'white', text: SCORE_TEXT_DARK }
    }
  }
}

export const getGolferScore = (
  points: number | string,
  isPTWGame?: boolean,
  scoring_option?: ScoringOption,
) => {
  if (isPTWGame) {
    switch (scoring_option) {
      case 'GOLD':
        return { points, color: '#50CA81' }
      case 'SILVER':
        return { points, color: '#FFDE2D' }
      case 'BRONZE':
        return { points, color: SCORE_ORANGE }
      case 'ZERO':
        return { points, color: '#E40145' }
      default:
        return { points, color: 'white' }
    }
  } else {
    switch (true) {
      case typeof points === 'number' && points > 0:
        return {
          points: `${points}`,
          color: '#F5535C',
        }
      case points === 0:
        return { points: 'E', color: '#FFDE2D' }
      case typeof points === 'number' && points < 0:
        return { points, color: '#50CA81' }
      case points === '-':
        return { points: '-', color: 'black' }
      default:
        return { points: '-', color: 'black' }
    }
  }
}

export const getGolferWordByScore = (points: number | null, isPTWGame?: boolean) => {
  const scoreWriter = {
    10: 'GOLD',
    4: 'SILVER',
    2: 'BRONZE',
    0: 'ZERO',
  } as const
  if (isPTWGame) {
    return scoreWriter[points as keyof typeof scoreWriter]
  }

  if (points === null) {
    return null
  }
  switch (true) {
    case points > 0:
      return 'BRONZE'
    case points === 0:
      return 'ZERO'
    case points < 0:
      return 'GOLD'
    default:
      return 'SILVER'
  }
}

const isAtLeastOneCheckboxSelected = (question: Question, formData: FormData) => {
  if (question.type === 'CHECKBOX') {
    const checkboxQuestion = question as CheckboxQuestion
    return checkboxQuestion.options.some((option) => {
      const key = `${checkboxQuestion.id}_${option.id}_${option.description}_${question.workflow_id}`
      return formData[key] === true
    })
  }
  return true
}

export const isWorkflowValid = (questions: Question[], formData: FormData) => {
  return questions.every((question) => isAtLeastOneCheckboxSelected(question, formData))
}

export const getDefaultWorkflowValues = (questions: Question[]) => {
  if (!questions.length) {
    return
  }

  return questions.reduce((acc, question) => {
    if (question.type === QuestionType.CHECKBOX) {
      question.options.forEach((option) => {
        const key = `${question.id}_${option.id}_${option.description}_${question.workflow_id}`
        acc[key] = false
      })
    } else {
      const key = `${question.id}_${question.workflow_id}`
      acc[key] = ''
    }
    return acc
  }, {} as FormData)
}

export const parseFormData = (data: FormData, gameId: number, gate: string | null) => {
  return Object.entries(data)
    .filter(([_, value]) => value !== false)
    .map(([key, value]) => {
      if (key.split('_').length > 3) {
        const [questionId, optionId, option, workflowId] = key.split('_')
        return {
          id: Number(questionId),
          gate,
          workflow_id: workflowId ? Number(workflowId) : null,
          question_option_id: Number(optionId),
          value: option,
          game_id: gameId,
        }
      }
      const [id, workflowId] = key.split('_')
      if (typeof value === 'string' && value.split('_').length > 1) {
        const [optionId, option] = value.split('_')
        return {
          id: Number(id),
          gate,
          value: option,
          workflow_id: Number(workflowId),
          question_option_id: Number(optionId),
          game_id: gameId,
        }
      }
      return {
        id: Number(id),
        gate,
        workflow_id: Number(workflowId),
        value: value as string,
        game_id: gameId,
      }
    })
}

export const parsePenalty = (playerData: string, playerId: number) => {
  const penalty = playerData?.[playerId]
  return penalty ?? 0
}

export const getOrdinalFromNumber = (number: number | string) => {
  return number === '-' || number === 0
    ? ''
    : ['st', 'nd', 'rd'][((((Number(number) + 90) % 100) - 10) % 10) - 1] || 'th'
}

export const parseStatsData = (statsData: GameStatistics[], gameInfo: GameINFOV2) => {
  switch (gameInfo.selection_type) {
    case GameType.PTW:
      return (
        (statsData?.[0]?.selections.sort(
          (a, b) => b?.percentage - a?.percentage,
        ) as SelectionStatWithUnit[]) ?? []
      )
    case GameType.ODDS:
      return (
        (statsData?.[0]?.selections?.sort(
          (a, b) => b?.percentage - a?.percentage,
        ) as SelectionStatWithUnit[]) ?? []
      )
    case GameType.DRAFT:
      return (
        (statsData?.[0]?.selections.sort(
          (a, b) => b?.percentage - a?.percentage,
        ) as SelectionStatWithUnit[]) ?? []
      )
    case GameType.SR:
      return (
        (statsData?.[0]?.selections.sort(
          (a, b) => b?.percentage - a?.percentage,
        ) as SelectionStatWithHomeAwayTeams[]) ?? []
      )
    default:
      return []
  }
}

export const generateLeagueLink = (
  leagueId: number,
  slug: string,
  isMiniLeague: boolean,
  gameId?: number,
) => {
  if (typeof window !== 'undefined') {
    const link = isMiniLeague
      ? `${window.location.origin}/${slug}/mini-league/${leagueId}`
      : `${window.location.origin}/${slug}/games/${gameId}`

    return link
  }
  return ''
}

export const isMiniLeague = (game: GameINFOV2) => {
  return (game as GameINFOV2).parent_game_id !== null
}

export const isRoundJoined = (
  game: Pick<GameINFOV2, 'rounds_joined'>,
  roundId: number | undefined,
  isUserJoined: boolean,
) => {
  if (!isUserJoined) return false

  return game.rounds_joined !== undefined
    ? game.rounds_joined.find((r) => r.round_id === roundId)?.is_user_joined ?? false
    : isUserJoined
}

export const getRoundState = (round: SimpleRound) => {
  const now = new Date().getTime()
  const roundStart = new Date(round.start_date).getTime()

  if (now < roundStart) {
    return `${new Date(round.start_date).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })} ${new Date(round.start_date).toLocaleDateString()}`
  }
  return RoundStatusLabels[round.status]
}

export const isDeadlinePassed = (round: SimpleRound | null) => {
  if (!round) {
    return { isDeadlinePassed: false }
  }
  const isDeadlinePassed = new Date(round.start_date).getTime() < new Date().getTime()
  return { isDeadlinePassed }
}

export const isGameStarted = (game: GameINFOV2 | null) => {
  if (!game) return false
  const gameStarted = new Date(game?.start_time).getTime() < new Date().getTime()
  return gameStarted
}

export const isRoundStarted = (round: SimpleRound) => {
  const gameStarted = new Date(round.start_date).getTime() < new Date().getTime()
  return gameStarted
}

export const isBattleRoyaleGame = (game: { is_battle_royale?: boolean } | null | undefined) =>
  !!game?.is_battle_royale

export const isLeaderboardData = (
  record: LeaderboardRecord[] | Participant[],
): record is LeaderboardRecord[] => {
  return !!(record as LeaderboardRecord[])[0].result
}

export function sanitizeEmail(email: string | string[] | undefined): string {
  if (!email) return ''

  const emailStr = Array.isArray(email) ? email[0] : email

  if (!emailStr) return ''

  const cleanEmail = emailStr.split('?')[0].split('&')[0]

  try {
    return decodeURIComponent(cleanEmail).trim()
  } catch {
    return cleanEmail.trim()
  }
}

export const isLeaderboardRecord = (
  record: LeaderboardRecord | Participant,
): record is LeaderboardRecord => {
  return !!(record as LeaderboardRecord).payout
}

export const getAllUnitsFromRound = (round: Round) => {
  return round.matches.flatMap((match) => match.units)
}

export const isAllSelectionInSpecificRound = (
  game: GameINFOV2,
  round: Round | null,
  selections: Array<SelectionUpdateObj>,
) => {
  if (!round) return false

  const correctSelectionsLength = game.selection_type === GameType.SR ? 2 : 1
  const firstRoundMatches = round.matches.map((r) => r.id).sort()
  const selectionMatches = selections
    .map((sel) => (sel.round_id === round.id ? sel.match_id : null))
    .filter((sel) => sel)
    .sort()
  return firstRoundMatches.length === selectionMatches.length / correctSelectionsLength
}

export const getStepsConfig = (page: PartnerPageData) => {
  const charityBreadcrumbName = !page.charity_page_breadcrumb_name
    ? STEPS.CHARITY
    : page.charity_page_breadcrumb_name

  return [
    { step: STEPS.ACCOUNT, flags: [] },
    { step: STEPS.FORM, flags: ['wufoo_forms'] },
    {
      step: charityBreadcrumbName,
      flags: ['sign_up_charity_page'],
    },
  ] satisfies Array<{ step: string; flags: Array<keyof FeatureFlags> }>
}

export const getStepperItems = (page: PartnerPageData, flowType: string) => {
  if (flowType === FlowTypes.AUTH) {
    return [STEPS.ACCOUNT]
  }

  const stepsConfig = getStepsConfig(page)

  return stepsConfig
    .filter((stepConfig) => stepConfig.flags.every((flag) => page.feature_flags[flag]))
    .map((stepConfig) => stepConfig.step)
}

export const checkContrast = (color: string) => {
  const hex = color.replace('#', '')
  const c_r = parseInt(hex.substring(0, 0 + 2), 16)
  const c_g = parseInt(hex.substring(2, 2 + 2), 16)
  const c_b = parseInt(hex.substring(4, 4 + 2), 16)
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000
  return brightness > 155
}

export const getPassedRounds = (game: GameINFOV2) => {
  const isDeadlinePassed = (round: SimpleRound) => {
    return new Date(round.start_date).getTime() < new Date().getTime()
  }
  const rounds = game.rounds.filter(isDeadlinePassed)

  return rounds
}

export const getSortedLeaderboard = (leaderboardData: LeaderboardRecord[]) => {
  const sortedLeaderboardData = leaderboardData.sort((a, b) => {
    if (a.position === '0' && b.position !== '0') {
      return 1
    }
    if (a.position !== '0' && b.position === '0') {
      return -1
    }
    return 0
  })
  return sortedLeaderboardData
}

export const getNotFinishedRounds = (game: GameINFOV2) => {
  return game.rounds.filter((round) => round.status !== RoundStatus.FINISHED)
}

export function generateGradient(startColor: string, direction = 'to right') {
  function hexToRgb(hex: string) {
    hex = hex.replace('#', '')
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return [r, g, b]
  }

  function rgbToHex(r: number, g: number, b: number): string {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
  }

  function lightenColor(color: string, percent: number): string {
    let [r, g, b] = hexToRgb(color)
    r = Math.min(255, Math.floor(r + ((255 - r) * percent) / 100))
    g = Math.min(255, Math.floor(g + ((255 - g) * percent) / 100))
    b = Math.min(255, Math.floor(b + ((255 - b) * percent) / 100))
    return rgbToHex(r, g, b)
  }

  const endColor = lightenColor(startColor, 20)

  return `linear-gradient(${direction}, ${startColor}, ${endColor})`
}

export const getBgImageBySport = (sport: string | undefined) => {
  if (!sport) {
    return null
  }

  const footballImages = [
    '/sports/football/1.png',
    '/sports/football/2.png',
    '/sports/football/3.png',
  ]
  const rugbyImages = ['/sports/rugby-union/1.png', '/sports/rugby-union/2.png']

  const sportMap = {
    Football: footballImages[Math.floor(Math.random() * footballImages.length)],
    Golf: '/sports/golf/1.webp',
    Cricket: '/sports/cricket/1.png',
    'Rugby Union': rugbyImages[Math.floor(Math.random() * rugbyImages.length)],
    'Rugby League': '/sports/rugby-league/1.jpg',
  }

  return sportMap[sport as keyof typeof sportMap] ?? null
}

export function getGolfHierarchyData(data: GolfData | undefined) {
  if (!data) {
    return null
  }

  if (data.status === 'CUT' || data.status === 'WD') {
    return { label: 'Status', data: data.status === 'CUT' ? 'MC' : 'WD' }
  }

  const currentRound = data.rounds[data.current_round_number ?? 0]
  const roundStrokes = currentRound?.strokes

  if (roundStrokes !== null && roundStrokes > 0) {
    return { label: 'Round score', data: String(roundStrokes) }
  }

  if (data.holes_played !== null && data.holes_played > 0) {
    return { label: 'Thru', data: String(data.holes_played) }
  }

  if (data.current_round_number !== null && data.rounds[data.current_round_number]) {
    const currentRound = data.rounds[data.current_round_number]
    if (currentRound.tee_time !== null) {
      return { label: 'Tee Time', data: currentRound.tee_time }
    }
  }

  return null
}

export const getShareButtonLabel = (roundStatus: keyof typeof RoundStatus) =>
  roundStatus === RoundStatus.UPCOMING ? 'Share your picks' : 'Share your results'

export function reverseRoundsList(rounds: SimpleRound[]): SimpleRound[] {
  const roundsToReverse = [...rounds]
  const reversed = roundsToReverse.reverse()
  return reversed
}

export const toQueryString = (params: Record<string, string | undefined>) =>
  new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<
      string,
      string
    >,
  ).toString()

export function itemKey(game: GameWithKind) {
  return game.kind === 'round' ? `${game.id}-round-${game.roundId}` : String(game.id)
}

export const adapterIPrizeDrawTicketToITicket = (ticket: IPrizeDrawTicket): ITicket => {
  return {
    id: ticket.id,
    ticket_number: ticket.ticket_number,
    hasWon: ticket.instant_win !== null,
    is_winner: ticket.is_winner,
    prize: ticket.instant_win?.prize_pool
      ? {
          id: ticket.instant_win.prize_pool.prize_name,
          name: ticket.instant_win.prize_pool.prize_name,
          isMainPrize: false,
          value: ticket.instant_win.prize_pool.prize_value,
          type: ticket.instant_win.prize_pool.prize_type,
        }
      : undefined,
  }
}
